import { BreadcrumbItem, Breadcrumbs } from "@vds/breadcrumbs";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { Title, Body } from "@vds/typography";
import { Tilelet } from "@vds/tiles";
import { Icon } from "@vds/icons";
import { ReactComponent as SupportIcon } from "../../assets/supportIcon.svg";
import { useDispatch } from "react-redux";
import { showNotificationBanner } from "../../store/actions";
import axios from "axios";
import { device } from "../../styles/breakpoints";

const SupportContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  width: 100%;

  @media ${device.tablet} {
    padding: 20px;
  }

  @media ${device.desktop} {
    padding: 24px;
  }
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  margin-bottom: 16px;

  @media ${device.tablet} {
    margin-bottom: 20px;
  }
`;

const HelpdeskContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 24px 0;
  align-items: flex-start;

  @media ${device.tablet} {
    flex-direction: row;
    gap: 16px;
    margin: 32px 0;
    align-items: center;
  }

  @media ${device.desktop} {
    margin: 40px 0;
  }
`;

const CardContainer = styled.div`
  /* Mobile: single column */
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  width: 100%;
  /* Prevent grid items from stretching */
  align-items: start;

  @media ${device.tablet} {
    /* Tablet: 2 columns */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  @media ${device.desktop} {
    /* Desktop: 4 columns */
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
  }
`;

const TileletCard = styled.div`
  position: relative;
  height: 220px;

  @media ${device.desktop} {
    height: 196px;
  }
`;

const DownloadIconWrapper = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  pointer-events: none;
  z-index: 3;
`;

const Support = () => {
  const dispatch = useDispatch();
  const supportItems = [
    {
      title: "User Guide",
      description:
        "This guide will help you navigate and use the 5G Video Insights portal with ease.",
      href: window.REACT_APP_USER_GUIDE_DOCUMENTATION || "",
    },
    {
      title: "Technical Documentation",
      description:
        "This guide details key technical considerations when it comes to deploying 5G Video Insights.",
      href: window.REACT_APP_TECHNICAL_DOCUMENTATION || "",
    },
    {
      title: "App Guide",
      description:
        "This guide provides examples and illustrations as to what you can do with our Computer Vision Applications.",
      href: window.REACT_APP_APP_GUIDE_DOCUMENTATION || "",
    },
    {
      title: "Hardware installation & set-up",
      description: "A hardware overview and installation guide",
      href: window.REACT_APP_HARDWARE_INSTALLATION_DOCUMENTATION || "",
    },
  ];

  const handleDownload = async (fetchUrl, fileName) => {
    const formatTitleForFileName = (fileName) => {
      return fileName?.replace(/\s+/g, "_") + ".pdf";
    };
    const formattedTitle = formatTitleForFileName(fileName);
    try {
      const response = await axios.get(fetchUrl, {
        responseType: "blob",
      });
      if (!response?.status === 200) throw new Error("Network error");
      const url = window.URL.createObjectURL(new Blob([response?.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = formattedTitle;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showPermissionsBanner(true);
      console.error("Download failed:", error);
    }
  };

  const showPermissionsBanner = () => {
    const notificationInfo = {
      type: "Document download failed",
      priority: "critical",
      showNotification: true,
    };
    dispatch(showNotificationBanner(notificationInfo));
  };

  return (
    <SupportContainer>
      <BreadcrumbContainer>
        <Breadcrumbs surface="light">
          <BreadcrumbItem>
            <Link
              data-testid="support_settingsLink"
              to="/settings"
              className="breadcrums-item_link"
            >
              Settings
            </Link>
          </BreadcrumbItem>
          <BreadcrumbItem>Support</BreadcrumbItem>
        </Breadcrumbs>
      </BreadcrumbContainer>
      <Title size="large">Support</Title>
      <HelpdeskContainer>
        <SupportIcon />
        <Body size="large">
          <b>Wireless Enterprise Helpdesk (BGCO):</b> Call <u>1-800-525-0481</u>
        </Body>
      </HelpdeskContainer>
      <CardContainer>
        {supportItems.map((item, index) => (
          <TileletCard key={index}>
            <Tilelet
              textAlignment="left"
              title={{
                bold: true,
                size: "titleSmall",
                children: item.title,
              }}
              subtitle={{
                size: "bodyLarge",
                children: item.description,
                color: "primary",
              }}
              surface="light"
              height="100%"
              data-testid="support_downloadDocumentTilelet"
              onClick={() => handleDownload(item.href, item.title)}
              backgroundColor="gray"
            />
            <DownloadIconWrapper>
              <Icon name="download" size="medium" />
            </DownloadIconWrapper>
          </TileletCard>
        ))}
      </CardContainer>
    </SupportContainer>
  );
};

export default Support;
